/**
 * Presets for hiding an AP invoice from the invoices list. The browser
 * computes tomorrow, three days, and one week at 9:00 local. `specific` is
 * a date and time the caller sends.
 */
export const AP_INVOICE_SNOOZE_PRESETS = [
  "tomorrow",
  "three_days",
  "one_week",
  "specific",
] as const;

export type APInvoiceSnoozePreset = (typeof AP_INVOICE_SNOOZE_PRESETS)[number];

/**
 * Returned when a snooze needs the caller to confirm a warning first.
 * Open tasks on the invoice stay visible, and a time past the due date
 * does not move the due date.
 */
export const INVOICE_SNOOZE_CONFIRMATION_REQUIRED =
  "INVOICE_SNOOZE_CONFIRMATION_REQUIRED";

/** True while `snoozedUntil` is still in the future. A missing or invalid time is not. */
export function isActivelySnoozed(
  snoozedUntil: Date | string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (snoozedUntil == null) return false;
  const until =
    snoozedUntil instanceof Date ? snoozedUntil : new Date(snoozedUntil);
  return until.getTime() > now.getTime();
}

export enum APInvoiceStatus {
  DRAFT = "DRAFT",
  MATCHED = "MATCHED",
  ACCEPTED = "APPROVED",
  FINALIZED = "FINALIZED",
  VOID = "VOID",
  /**
   * Operator-ignored: a draft, matched, or accepted invoice created in
   * error, or one we do not want to take action on. Unpaid FINALIZED invoices
   * can be ignored only when the caller opts in. Hidden from the default
   * working queue. Distinct from VOID, which is reserved for an accounting void.
   * Unignore restores the status recorded just before this one.
   */
  IGNORED = "IGNORED",
}

/** Statuses a person may snooze. Only drafts for now. */
export const SNOOZABLE_AP_INVOICE_STATUSES = [APInvoiceStatus.DRAFT] as const;

export type SnoozableAPInvoiceStatus =
  (typeof SNOOZABLE_AP_INVOICE_STATUSES)[number];

/**
 * `outcomeReason` on tasks cancelled because their AP invoice was ignored.
 * Unignore reopens only tasks stamped with this reason.
 */
export const AP_INVOICE_IGNORED_TASK_OUTCOME_REASON = "Invoice ignored";

/**
 * Locked-in confidence classification for AP invoice line item matches.
 * Persisted in EntityAssociation.metadata.matchStatus at match creation time
 * so threshold changes don't retroactively reclassify existing matches.
 */
export enum MatchConfidenceStatus {
  CONFIRMED = "confirmed",
  TENTATIVE = "tentative",
}

export enum PaymentStatus {
  UNPAID = "unpaid",
  PARTIALLY_PAID = "partially_paid",
  PAID = "paid",
  OVERPAID = "overpaid",
}

/**
 * Payment-status filter value for an invoice still owed (unpaid or partially
 * paid) past its due date. Derived, never stored on the invoice.
 */
export const OVERDUE_PAYMENT_STATUS_FILTER = "overdue";

/**
 * Status of synchronization to external origin systems (e.g., Famous ERP)
 * Used to track the lifecycle of syncing entities to external systems
 */
export enum OriginSyncStatus {
  NotStarted = "not_started",
  Pending = "pending",
  InProgress = "in_progress",
  Success = "success",
  Failed = "failed",
  Retrying = "retrying",
}

/**
 * Type of external payment for invoices that are paid outside of Gently
 * (e.g., customer has set up auto-pay directly with the vendor)
 */
export enum ExternalPaymentType {
  /** Automatic payment set up by customer with vendor (e.g., utility bills) */
  Autopay = "autopay",
  /** Recurring scheduled payment */
  Recurring = "recurring",
  /** Manual payment made outside of Gently */
  ManualExternal = "manual_external",
}
